/**
 * QR Code Encoding/Decoding for Production App
 *
 * Implements THP:// protocol from QR-Spec.md.
 * Base64-encoded JSON payload with version control.
 *
 * VERSION MIGRATION STRATEGY:
 * When upgrading QR schema in future:
 * 1. Increment CURRENT_QR_VERSION
 * 2. Add new fields to schema (keep old fields for backwards compat)
 * 3. Update decodeGarmentQR to handle both versions
 * 4. Old labels remain scannable (graceful degradation)
 *
 * Current version: 2 (Phase 50 / BLU-233 — DTF)
 * - `m` (print technology) widened from literal 'DTG' to enum ['DTG','DTF']
 * - V1 labels remain decodable (decodeGarmentQR branches on payload.v)
 * - POD and P&P fulfillment types
 */

import { z } from 'zod';

/** Current QR payload version. Increment when schema changes. */
export const CURRENT_QR_VERSION = 2;

/** Protocol prefix for ThreadHeads Production QR codes */
export const QR_PREFIX = 'THP://';

/**
 * QR payload schema with strict validation.
 * Keep payload minimal to ensure QR remains scannable.
 *
 * Fields:
 * - v: Version number (for future schema evolution)
 * - g: garment_unit_id (UUID)
 * - o: order_number (e.g., "TH-104923")
 * - s: SKU (e.g., "TSHIRT-BLK-001")
 * - m: print_technology axis ('DTG' in V1; 'DTG'|'DTF' in V2). NOTE: this is the
 *      print-technology hint, NOT production_method (POD|PAP) — the fulfilment
 *      method rides `t`. Routing hint only: it's baked in at label-print time
 *      and goes stale if the unit's DB print_technology is corrected
 *      afterward — station scan-routing reads this field as a first guess,
 *      but the DB value is authoritative and supersedes it whenever they
 *      disagree (see useTechAwareScanRouting.ts's DB-trust note).
 * - t: fulfillment_type (POD or P&P)
 */
export const qrPayloadSchema = z.object({
  v: z.literal(CURRENT_QR_VERSION),
  g: z.string().uuid(),           // garment_unit_id
  o: z.string().min(1).max(50),   // order_number
  s: z.string().min(1).max(50),   // SKU
  m: z.enum(['DTG', 'DTF']),      // print_technology - V2 widened from literal 'DTG'
  t: z.enum(['POD', 'P&P']),      // fulfillment_type
});

export type GarmentQRPayload = z.infer<typeof qrPayloadSchema>;

/**
 * V1 payload schema — kept for backwards-compatible decoding of labels printed
 * before the V2 bump. `m` was pinned to literal 'DTG' in V1. V1 labels are still
 * out in the wild (printed/stored), so decodeGarmentQR must accept them.
 */
export const qrPayloadSchemaV1 = z.object({
  v: z.literal(1),
  g: z.string().uuid(),
  o: z.string().min(1).max(50),
  s: z.string().min(1).max(50),
  m: z.literal('DTG'),
  t: z.enum(['POD', 'P&P']),
});

export type GarmentQRPayloadV1 = z.infer<typeof qrPayloadSchemaV1>;

/** A decoded garment QR — either the current V2 payload or a legacy V1 one. */
export type DecodedGarmentQRPayload = GarmentQRPayload | GarmentQRPayloadV1;

/**
 * Encode garment data to QR string.
 * Format: THP:// + Base64(JSON)
 *
 * @param payload - Validated garment payload
 * @returns QR string ready for QRCodeSVG component
 * @throws ZodError if payload is invalid
 *
 * @example
 * const qr = encodeGarmentQR({
 *   v: 2,
 *   g: 'uuid-here',
 *   o: 'TH-104923',
 *   s: 'TSHIRT-BLK-001',
 *   m: 'DTG',
 *   t: 'POD',
 * });
 * // Returns: "THP://eyJ2IjoyLCJnIjoiLi4uIn0="
 */
export function encodeGarmentQR(payload: GarmentQRPayload): string {
  // Validate before encoding to catch errors early
  qrPayloadSchema.parse(payload);
  const json = JSON.stringify(payload);
  const base64 = btoa(json);
  return `${QR_PREFIX}${base64}`;
}

/**
 * Decode QR string to garment payload.
 * Returns null for invalid format or version mismatch.
 *
 * @param qrString - Scanned QR code string
 * @returns Decoded payload or null if invalid
 *
 * @example
 * const payload = decodeGarmentQR('THP://eyJ2IjoxLC4uLn0=');
 * if (payload) {
 *   console.log(payload.g); // garment_unit_id
 * }
 */
export function decodeGarmentQR(qrString: string): DecodedGarmentQRPayload | null {
  // Validate prefix
  if (!qrString.startsWith(QR_PREFIX)) {
    // Not a ThreadHeads QR code - this is expected for other QR types
    return null;
  }

  try {
    const base64 = qrString.slice(QR_PREFIX.length);
    const json = atob(base64);
    const payload = JSON.parse(json);

    // Version-tolerant decode: V1 labels are still in the wild (printed/stored)
    // and must remain scannable after the V2 bump. Branch on payload.v and run
    // the matching schema; unknown versions are rejected.
    if (payload.v === 1) {
      return qrPayloadSchemaV1.parse(payload);
    }
    if (payload.v === CURRENT_QR_VERSION) {
      return qrPayloadSchema.parse(payload);
    }
    return null;
  } catch {
    // Invalid format, encoding, or schema
    return null;
  }
}

/**
 * Check if a string is a valid garment QR code.
 *
 * @param qrString - String to check
 * @returns true if valid ThreadHeads garment QR
 */
export function isValidGarmentQR(qrString: string): boolean {
  return decodeGarmentQR(qrString) !== null;
}

/**
 * Create QR payload from garment data.
 * Convenience function for constructing payload object.
 *
 * @param garmentId - UUID of the garment unit
 * @param orderNumber - Order number (e.g., "TH-104923")
 * @param sku - Product SKU
 * @param fulfillmentType - POD or P&P
 * @param printTechnology - 'DTG' (default) | 'DTF'. Phase 50.01.3 / BLU-233:
 *   when the caller has the garment's real print_technology, pass it so the QR
 *   `m` field reflects the DTF/DTG axis. Defaults to 'DTG' for back-compat with
 *   every existing caller (no behavior change for DTG labels).
 * @returns Valid QR payload ready for encoding
 */
export function createQRPayload(
  garmentId: string,
  orderNumber: string,
  sku: string,
  fulfillmentType: 'POD' | 'P&P',
  printTechnology: 'DTG' | 'DTF' = 'DTG'
): GarmentQRPayload {
  return {
    v: CURRENT_QR_VERSION,
    g: garmentId,
    o: orderNumber,
    s: sku,
    // Print-technology hint (V2). Driven off the caller-supplied real value when
    // a DTF garment is involved; defaults to 'DTG'.
    m: printTechnology,
    t: fulfillmentType,
  };
}
